import { createMachine, assign, InterpreterFrom } from "xstate-4";
import { useActor } from "@xstate-4/vue";
import { xlog } from "vue-app/utils/xstate";
import { gtagSend } from "vue-app/plugins/edroneGtag";

export enum InstructionTypes {
  INBOX = "Inbox",
  WHATSAPP = "WhatsApp",
  MESSENGER = "Messenger",
}

type Context = {
  instructionType: InstructionTypes | null;
  instructionSteps: string[];
  inboundEmail: string;
};

export const machineInboxInstructions = createMachine(
  {
    id: "InboxInstructions",
    tsTypes: {} as import("./machineInboxInstructions.typegen").Typegen0,
    schema: {
      events: {} as
        | { type: "CLOSE" }
        | { type: "OPEN"; instructionType: InstructionTypes }
        | { type: "GO_TO_INSTRUCTION" },
      context: {} as Context,
    },
    context: {
      instructionType: null,
      instructionSteps: [
        window._trans._inbox.inboxIntegrations.notIntegrated.inbox.forwardInstructions.steps["1"],
        window._trans._inbox.inboxIntegrations.notIntegrated.inbox.forwardInstructions.steps["2"],
        window._trans._inbox.inboxIntegrations.notIntegrated.inbox.forwardInstructions.steps["3"],
        window._trans._inbox.inboxIntegrations.notIntegrated.inbox.forwardInstructions.steps["4"],
      ],
      inboundEmail: window.EDRONE_CONTEXT.appId + "@" + window.EDRONE_CONTEXT.user.subdomain + ".edrone.app",
    },
    initial: "closed",
    states: {
      closed: {
        tags: ["closed"],
        on: {
          OPEN: {
            target: "opened",
            actions: ["assignInstructionType", "gtag"],
          },
        },
      },
      opened: {
        tags: ["opened"],
        on: {
          GO_TO_INSTRUCTION: {
            actions: ["goToInstruction", "gtag"],
          },
          CLOSE: {
            target: "closed",
            actions: ["clearData"],
            cond: "canClose",
          },
        },
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignInstructionType: assign({
        instructionType: (context, event) => {
          xlog({ label: "assignInstructionType", type: "action", context, event });
          return event.instructionType;
        },
      }),
      clearData: assign({
        instructionType: () => {
          return null;
        },
      }),
      goToInstruction: (context, event) => {
        xlog({ label: "goToInstruction", type: "action", context, event });
        switch (context.instructionType) {
          case InstructionTypes.WHATSAPP:
            window.open("/app/integrations/whatsapp", "_blank");
            return;
          case InstructionTypes.MESSENGER:
            window.open("/app/integrations/messenger", "_blank");
            return;
          default:
            return;
        }
      },
      gtag: (context, event) => {
        xlog({ label: "gtag", type: "action", context, event });
        switch (event.type) {
          case "OPEN":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "plug",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "instructions",
              text: `${event.instructionType.toLowerCase()}_instruction`,
            });
            break;
          case "GO_TO_INSTRUCTION":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "instructions",
              text: `go_to_${context.instructionType?.toLowerCase()}_instruction`,
            });
            break;
          default:
            break;
        }
      },
    },
    guards: {
      canClose: (context, event, meta) => {
        xlog({ label: "canClose", type: "guard", context, event });
        return !meta.state.hasTag("getInboundEmail");
      },
    },
  }
);

export type InboxInstructionsActor = InterpreterFrom<typeof machineInboxInstructions>;

// @ts-ignore
export function useInboxInstructions(service) {
  return useActor(service.children.get("instructions") as InboxInstructionsActor);
}
